import React, { useMemo } from 'react';

import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import IconButton from '@mui/material/IconButton';
import withStyles from '@mui/styles/withStyles';

import SkillExpertiseEmployeeRemoval from 'UI/components/SkillExpertise/SkillExpertiseEmployeeRemoval';
import useConfig from 'UI/customHooks/useConfig';

import styles from './SkillExpertise.styles';
import SkillExpertiseEmployee from './SkillExpertiseEmployee';
import { useTooltipComponents } from './useTooltipComponents';
import classNames from 'classnames';
import get from 'lodash/get';

const buttonStyle = {
  border: 0,
  padding: 0,
  background: 'none',
};

const SkillExpertise = ({
  disabled,
  className,
  classes,
  level: actualLevel,
  interactive = false,
  allowZeroExpertise = false,
  onChange,
  skillType,
  synonymLevelDescriptions,
  isViewerOwner,
  suggested,
  suggestedBy,
  skillName,
  skillId,
  suggestedAction,
  readOnly,
  disableEdit,
  comment,
  handleDelete,
  disableTitleText,
  dataTest,
}) => {
  const config = useConfig();

  const supportedLevels = useMemo(() => get(config, 'cms.features.skills.expertiseLevels'), [
    config,
  ]);
  const tooltipComponents = useTooltipComponents({
    skillType,
    supportedLevels,
    synonymLevelDescriptions,
    disableFocusListener: true,
    disableTitleText,
  });

  const hasValue = actualLevel && supportedLevels.indexOf(actualLevel) > -1;
  const isDisabled = !interactive || disabled;

  const expertiseChangeCallbacks = useMemo(() => {
    return supportedLevels.reduce((handlers, supportedLevel) => {
      handlers[supportedLevel] = event => {
        event.stopPropagation();

        if (interactive && !disabled) {
          onChange(allowZeroExpertise && actualLevel === supportedLevel ? null : supportedLevel);
        }
      };
      return handlers;
    }, {});
  }, [supportedLevels, interactive, disabled, onChange, actualLevel, allowZeroExpertise]);
  const suggestedLevel = useMemo(() => supportedLevels.indexOf(suggested), [
    suggested,
    supportedLevels,
  ]);

  if (suggested && suggestedAction !== 'delete' && isViewerOwner && !disableEdit) {
    return (
      <SkillExpertiseEmployee
        suggested={suggested}
        suggestedLevel={suggestedLevel}
        suggestedBy={suggestedBy}
        supportedLevels={supportedLevels}
        actualLevel={actualLevel}
        skillName={skillName}
        handleChangeExpertise={onChange}
        skillId={skillId}
        readOnly={readOnly}
        comment={comment}
      />
    );
  }

  if (suggestedAction === 'delete' && isViewerOwner && !disableEdit) {
    return (
      <SkillExpertiseEmployeeRemoval
        handleDelete={handleDelete}
        skillId={skillId}
        skillName={skillName}
        suggestedBy={suggestedBy}
        comment={comment}
        supportedLevels={supportedLevels}
        actualLevel={actualLevel}
      />
    );
  }

  return (
    <div
      className={classNames(
        classes.root,
        {
          [classes.interactive]: interactive,
          [classes.noValue]: !hasValue,
          [classes.cursorNotAllowed]: !disableTitleText && isDisabled,
        },
        className
      )}
      data-test={dataTest}
    >
      {supportedLevels.map((supportedLevel, index) => {
        const TooltipComponent = tooltipComponents[supportedLevel];
        return (
          <TooltipComponent key={supportedLevel}>
            <IconButton
              aria-label={`rate skill as ${supportedLevel}`}
              style={buttonStyle}
              onClick={expertiseChangeCallbacks[supportedLevel]}
              disabled={isDisabled}
              className={classNames({
                [classes.cursorNotAllowed]: !disableTitleText && isDisabled,
              })}
            >
              {index <= supportedLevels.indexOf(actualLevel) ? (
                <StarIcon data-test={`star-icon${index}`} className={classes.icon} />
              ) : (
                <StarBorderIcon data-test={`star-icon${index}`} className={classes.icon} />
              )}
            </IconButton>
          </TooltipComponent>
        );
      })}
    </div>
  );
};

export default withStyles(styles)(SkillExpertise);
